<template>
  <div class="column">
    <div
      ref="chatConversation"
      class="card card-chat chat-conversation"
      :class="[
        `${conversation.type === 'single' ? `card-chat-${userID} ${getStatusClass(user?.status)}` : ''}`,
        `${isCollapsed ? 'collapsed' : ''}`
      ]"
      :style="{ width: chatWidth + 'px', height: isCollapsed ? 'auto' : chatHeight + 'px', minHeight: isCollapsed ? '0' : 'auto' }"
      :data-room-id="conversation.id"
      @mousedown="startResize"
    >
      <conversation-header :room="conversation" :user="user" :authid="authid" @toggle="toggleConversation" @close="closeConversation" />
      <div :ref="`browse-file-${conversation.id}`" :class="`open-file-browser-${conversation.id}`" />

      <div :ref="`chat-${conversation.id}`" :class="`collapse overflow-auto ${isCollapsed ? 'h-auto' : 'h-100 show'}`">
        {{conversation.users}}
        <div v-show="showGroupForm" class="border-bottom pt-4 px-4 pb-2">
          <h5>Group Info</h5>
          <form @submit.prevent="updateGroupInfo" id="form-edit-room-float">
            <div v-if="isLoadingMembers" class="loading-spinner">
              Loading members...
            </div>
            <div v-else>
              <div class="form-group">
                <label>Title</label>
                <input type="text" v-model="title" name="title" class="form-control" required>
              </div>
              <div class="form-group">
                <label>Select Users</label>
                <v-select v-model="selectedUsers" class="select-users" multiple label="name" :options="userOptions" />
              </div>
              <div class="form-group text-center">
                <button type="submit" class="btn btn-sm btn-primary" :disabled="isUpdatingRoom">
                  <span v-if="isUpdatingRoom">Updating room...</span>
                  <span v-else>
                    <i class="fas fa-pen-nib"></i> Update
                  </span>
                </button>
                <button type="button" class="btn btn-sm btn-danger" @click="showGroupForm = false">
                  Close
                </button>
              </div>
            </div>
          </form>
        </div>
        <div @dragover="dragOverFile" @dragleave="dragLeaveFile" @drop="dropFile">
          <conversation-message :is-collapsed="isCollapsed" :room="conversation" :height="chatBoxHeight" />
          <div v-if="isDraggingFile" class="draggable-overlay" />
        </div>

        <div class="file-preview d-flex">
          <vue-dropzone
            :ref="`dropzone-${conversation.id}`"
            :id="'file-upload-' + conversation.id"
            :options="dropzoneOptions"
            @vdropzone-file-added="showAddedFile"
          />

          <div v-if="dropzoneOptions.fileName" class="file-name" :title="dropzoneOptions.fileName">
            {{ formattedFileName }}
          </div>

          <div v-if="previewUrl !== ''" class="send-file-action">
            <button class="btn btn-xs btn-success" :disabled="isSendingFile" @click="sendFile(conversation.id)">
              <i class="fas fa-check"></i> {{ isSendingFile ? 'Sending..' : 'Send' }}
            </button>
            <button class="btn btn-xs btn-danger" :disabled="isSendingFile" @click="removeFiles(conversation.id)">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <conversation-input :avatar="account_image" :room="conversation" :user="user" @file-browser="openFileBrowser" />
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import vueDropzone from "vue2-dropzone";
import Noty from 'noty';
import { eventBus, formatFileName } from "../../utils/helpers";
import {
  broadcastMessage,
  getUserInRoom,
  isCollapsed,
  isFloating,
  sendMessage,
  getBranches,
  getGroupMember,
  updateChatRoom,
} from "../../services/api/uni-chat";
import ConversationHeader from "./components/ConversationHeader.vue";
import ConversationMessage from "./components/ConversationMessage.vue";
import ConversationInput from "./components/ConversationInput.vue";

import "vue-select/dist/vue-select.css";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import {mapGetters} from "vuex";

export default {
  name: "ChatConversation",
  components: {
    vueDropzone,
    ConversationHeader,
    ConversationMessage,
    ConversationInput,
    'v-select': vSelect
  },
  props: {
    conversation: {
      type: [Array, Object],
      default: []
    },
    authid: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      user: [],
      userID: 0,
      status: '',
      statusLabel: '',
      account_image: '',
      isUpdatedByEvent: false,
      isCollapsed: false,
      isSendingFile: false,
      isDraggingFile: false,
      isResizing: false,
      previewUrl: '',
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      chatWidth: 300,
      chatHeight: 385,
      chatHeightExtension: 60,
      chatBoxHeight: 295,
      dropzoneFileAdded: false,
      showGroupForm: false,
      selectedUsers: [],
      branches: [],
      isUpdatingRoom: false,
      isLoadingMembers: false,
      title: this.conversation.title,
      dropzoneOptions: {
        url: `/admin/api/uni-chat/message/${this.conversation.id}/file`,
        maxFiles: 1,
        clickable: `.open-file-browser-${this.conversation.id}`,
        acceptedFiles: 'image/*,application/pdf,video/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        filePdfIcon: '/images/icons-file/pdf.png',
        fileDocxIcon: '/images/icons-file/docx.png',
        fileVideoIcon: '/images/icons-file/video.png',
        fileName: '',
        maxFilesize: 50,
        thumbnailWidth: 45,
        thumbnailHeight: 45,
        autoQueue: false,
        autoProcessQueue: false,
        uploadMultiple: false
      }
    }
  },
  async mounted() {
    this.isCollapsed = Boolean(this.conversation.pivot.is_collapsed);
    await this.getAllBranches();
    if (this.conversation.type === 'single') {
      await this.getUser(this.conversation.id);
    } else if (this.conversation.type === 'group') {
      this.isLoadingMembers = true;
      await this.getGroupMembers(this.conversation.id);
      this.isLoadingMembers = false;
    }

    eventBus.$on('collapse-chat-room', (room_id, is_collapsed) => {
      if (!this.isUpdatedByEvent && this.conversation.id === room_id) {
        this.isCollapsed = Boolean(is_collapsed);
      }
    });

    eventBus.$on('close-chat-room', (room_id) => {
      if (this.$refs.chatConversation && this.conversation.id === room_id) {
        this.$refs.chatConversation.parentElement.remove();
      }
    });

    eventBus.$on('toggle-chat-form', (room) => {
      if (this.$refs.chatConversation && this.conversation.id === room.id) {
        this.showGroupForm = !this.showGroupForm;
      }
    });
  },
  computed: {
    ...mapGetters('presence', ['getUserPresence']),
    formattedFileName() {
      return formatFileName(this.dropzoneOptions.fileName);
    },
    userOptions() {
      const options = [];
      this.branches.forEach(branch => {
        branch.users.forEach(user => {
          options.push({
            id: user.id,
            name: user.name
          });
        });
      });
      return options;
    }
  },
  watch: {
    conversation(value) {
      if (this.conversation.type === 'single') {
        this.getUser(value.id);
      } else if (this.conversation.type === 'group') {
        this.isLoadingMembers = true;
        this.getGroupMembers(value.id).then(() => {
          this.isLoadingMembers = false;
        });
      }
    }
  },
  methods: {
    async getUser(room_id) {
      try {
        const response = await getUserInRoom(room_id);

        const presence = this.getUserPresence(response.data.id)

        if (presence) {
          this.user = presence
          this.userID = presence.id
          this.statusLabel = presence.status_label
        } else {
          this.user = response.data;
          this.userID = response.data.id;
          this.statusLabel = response.data.status_label;
        }

        this.account_image = response.data.account_image;
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    },
    async getGroupMembers(room_id) {
      try {
        const response = await getGroupMember(room_id);
        const userIds = response.data;

        this.selectedUsers = this.userOptions.filter(user => userIds.includes(user.id));

      } catch (error) {
        console.log('error');
        console.error('Error fetching group members:', error);
      }
    },
    async updateGroupInfo() {
      try {
        this.isUpdatingRoom = true;

        const userIds = this.selectedUsers.map(user => user.id);
        const payload = {
          type: 'group',
          title: this.title,
          users: userIds,
          room_id: this.conversation.id
        };

        const response = await updateChatRoom(payload);

        if (response.status === 200) {
          this.showGroupForm = false;

          new Noty({
            type: 'success',
            text: response.data.message,
            timeout: 3000
          }).show();
        }
      } catch (error) {
        console.error("Error updating chat room:", error);
      } finally {
        this.isUpdatingRoom = false;
      }
    },
    async getAllBranches() {
      try {
        this.isLoading = true;

        const response = await getBranches();
        this.branches = response.data.data;
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        this.isLoading = false;
      }
    },
    getStatusClass(status) {
      const statusMap = {
        'online': 'is-active',
        'training': 'is-training',
        'lunch': 'is-lunch',
        'dnd': 'is-dnd',
        'busy': {
          'Video Call': 'is-video-call',
          'Call': 'is-call',
          'Visit': 'is-visit'
        }
      };

      // Check if the status is 'busy' and if statusLabel exists
      if (status === 'busy' && statusMap['busy'][this.statusLabel]) {
        return statusMap['busy'][this.statusLabel];
      }

      return statusMap[status] || 'is-inactive';
    },
    toggleConversation(room_id, is_collapsed) {
      this.isCollapsed = is_collapsed;
      this.isUpdatedByEvent = false;

      this.isCollapsedEvent(room_id, is_collapsed ? 1 : 0 );
    },
    isCollapsedEvent(room_id, is_collapsed) {
      this.isUpdatedByEvent = true;
      isCollapsed({ room_id, is_collapsed });
    },
    closeConversation(room) {
      this.$refs.chatConversation.parentElement.remove();
      isFloating({ room_id: room, is_floating: false })
    },
    openFileBrowser(room_id) {
      const dropzone = this.$refs[`dropzone-${room_id}`];

      if (dropzone && dropzone.getAcceptedFiles().length >= this.dropzoneOptions.maxFiles) {
        console.error("Maximum number of files exceeded!");
        return;
      }

      this.$refs[`browse-file-${room_id}`].click();
    },
    removeFiles(room_id, adjustChatHeight = true) {
      this.previewUrl = '';
      this.chatHeight -= adjustChatHeight ? this.chatHeightExtension : 0;
      this.dropzoneFileAdded = false;
      this.$refs[`dropzone-${room_id}`].removeAllFiles();
    },
    showAddedFile(file) {
      if (!file && !file.upload && !file.upload.filename) {
        console.error('Event is undefined');
        return;
      }

      // Assign the file name to the fileName variable
      this.dropzoneOptions.fileName = file.upload.filename;

      // Set the preview URL based on the file extension
      switch (file.upload.filename.split('.').pop().toLowerCase()) {
        case 'pdf':
          this.previewUrl = this.dropzoneOptions.filePdfIcon;
          break;
        case 'docx':
          this.previewUrl = this.dropzoneOptions.fileDocxIcon;
          break;
        case 'mp4':
        case 'avi':
        case 'mov':
          this.previewUrl = this.dropzoneOptions.fileVideoIcon;
          break;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          this.previewUrl = file.dataURL;
          break;
        default:
          this.removeFiles(this.conversation.id, this.dropzoneFileAdded);
          return;
      }

      // If files are added for the first time, adjust chat height
      if (!this.dropzoneFileAdded) {
        this.dropzoneFileAdded = true;
        this.chatHeight += this.chatHeightExtension;
      }

      // Update the preview image in the UI
      const dropzoneImg = document.querySelector('.dropzone img[data-dz-thumbnail]');
      if (dropzoneImg) {
        dropzoneImg.src = this.previewUrl;
        dropzoneImg.style.width = this.dropzoneOptions.thumbnailWidth + 'px';
        dropzoneImg.style.height = this.dropzoneOptions.thumbnailHeight + 'px';
      }
    },
    dragOverFile(event) {
      event.preventDefault();
      this.isDraggingFile = true;
    },
    dragLeaveFile(event) {
      event.preventDefault();
      const relatedTarget = event.relatedTarget;

      if (!this.$el.contains(relatedTarget)) {
        this.isDraggingFile = false;
      }
    },
    dropFile(event) {
      event.preventDefault();
      this.isDraggingFile = false;

      const dropzone = this.$refs[`dropzone-${this.conversation.id}`];

      if (dropzone) { // Check if dropzone instance is available
        const files = event.dataTransfer.files;

        // Check if only one file is dragged
        if (files.length > 1) {
          console.warn("Only one file can be dropped. Ignoring additional files.");
        }

        // Get the first file
        const file = files[0];

        // Remove previously added file
        dropzone.removeAllFiles();
        dropzone.addFile(file);
      } else {
        console.error("Dropzone instance not available.");
      }
    },
    async sendFile(room_id) {
      try {
        this.isSendingFile = true;

        // Access the files added to the dropzone
        const files = this.$refs[`dropzone-${room_id}`].getAcceptedFiles();

        if (files.length === 0) {
          console.error('No files added to dropzone.');
          return;
        }

        // Create FormData object and append each file
        const formData = new FormData();
        files.forEach(file => {
          formData.append('file', file, file.name);
          formData.append('file_name', this.dropzoneOptions.fileName);
        });

        const response = await sendMessage(room_id, formData);

        if (response.status === 200) {
          this.previewUrl = '';
          this.$refs[`dropzone-${room_id}`].removeAllFiles();

          const channel = new BroadcastChannel('chat');
          channel.postMessage({
            type: 'sending-message',
            room_id: room_id,
            message: response.data.message
          });

          // Broadcast newly sent message based on new Message ID
          await broadcastMessage(room_id, { message_id: response.data.message.id });
        }
      } catch (error) {
        if (error.response && error.response.status === 413) {
          new Noty({
            text: 'File size exceeds the maximum allowed limit.',
            type: 'warning',
            timeout: 3000
          }).show();

          // Remove the file from dropzone on 413 error
          this.$refs[`dropzone-${room_id}`].removeAllFiles();
        } else {
          console.error('Error uploading file:', error);
        }
      } finally {
        this.isSendingFile = false;
        this.dropzoneFileAdded = false;
        this.chatHeight = this.chatHeight - this.chatHeightExtension;
      }
    },
    startResize(event) {
      // Get the bounding rectangle of the chatConversation element
      const rect = this.$refs.chatConversation.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      const resizeMargin = 5;

      // Check if the cursor is within the resize margin of the borders
      const isTopBorder = mouseY >= rect.top && mouseY <= rect.top + resizeMargin;
      const isLeftBorder = mouseX >= rect.left && mouseX <= rect.left + resizeMargin;
      const isTopLeftCorner = isTopBorder && isLeftBorder;

      // Set cursor style based on resize direction
      if (isTopLeftCorner) {
        document.body.style.cursor = 'nw-resize';
      } else if (isTopBorder) {
        document.body.style.cursor = 'ns-resize';
      } else if (isLeftBorder) {
        document.body.style.cursor = 'ew-resize';
      } else {
        document.body.style.cursor = 'auto';
        return;
      }

      // Store initial resize parameters
      this.isResizing = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startWidth = this.$refs.chatConversation.offsetWidth;
      this.startHeight = this.$refs.chatConversation.offsetHeight;

      document.addEventListener('mousemove', this.resize);
      document.addEventListener('mouseup', this.stopResize);
    },
    resize(event) {
      if (this.isResizing) {
        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;

        const initChatWidth = 300;
        const initChatHeight = 385;
        const initChatBoxHeight = 295;

        const diffChatHeight = initChatHeight - initChatBoxHeight;

        // Resize based on cursor direction
        if (document.body.style.cursor === 'ew-resize') {
          this.chatWidth = Math.max(this.startWidth - deltaX, initChatWidth);
        } else if (document.body.style.cursor === 'ns-resize') {
          this.chatHeight = Math.max(this.startHeight - deltaY, initChatHeight);
          this.chatBoxHeight = Math.max((this.startHeight - diffChatHeight) - deltaY, initChatBoxHeight);
        } else if (document.body.style.cursor === 'nw-resize') {
          this.chatWidth = Math.max(this.startWidth - deltaX, initChatWidth);
          this.chatHeight = Math.max(this.startHeight - deltaY, initChatHeight);
          this.chatBoxHeight = Math.max((this.startHeight - diffChatHeight) - deltaY, initChatBoxHeight);
        }
      }
    },
    stopResize() {
      document.body.style.cursor = 'auto';

      this.isResizing = false;

      document.removeEventListener('mousemove', this.resize);
      document.removeEventListener('mouseup', this.stopResize);
    }
  }
}
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
</script>

<style lang="scss" scoped>
.chat-conversation {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  border: 1px solid #ccc;
  background-color: #fff;

  // Hover effect for top border
  &:hover::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: transparent;
    cursor: ns-resize;
  }

  // Hover effect for left border
  &:hover::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: transparent;
    cursor: ew-resize;
  }

  &.collapsed {
    height: auto !important;
    min-height: 0 !important;
  }

  &.resizable {
    .card {
      resize: both;
      overflow: auto;
    }

    // Conditionally apply height and min-height based on isCollapsed
    height: auto !important;
    min-height: 0 !important;
  }

  .collapse {
    &.show {
      height: 100%;
    }

    &:not(.show) {
      height: auto;
      min-height: 0;
    }
  }

  .draggable-overlay {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .file-preview {
    justify-content: space-between;
    align-items: center;
    padding: 10px 10px 10px 18px;

    .file-name {
      font-size: smaller;
    }
  }
}

::v-deep {
  .dropzone {
    border: none;
    min-height: 0;
    padding: 0;

    .dz-preview {
      min-height: 0;
      margin: 0;

      .dz-image {
        background: none;
        border-radius: 4px;

        img {
          border-radius: 4px;
        }
      }

      .dz-details, .dz-error-message {
        display: none;
      }

      .dz-progress {
        display: none;
        width: 30px;
        height: 10px;
        margin-top: -5px;
        margin-left: -15px;
        opacity: .5;
      }

      .dz-succcess-mark svg,  .dz-error-mark svg {
        width: 15px;
      }
    }
  }
}
</style>
                                                                                                                                                                                                                                                                        m  m m